import { Button, Modal, TextField } from '@mui/material';
import { styled } from '@mui/system';
import { FC, useMemo, useState } from 'react';
import { FcFolder, FcOpenedFolder } from 'react-icons/fc';
import { MdArrowBack } from 'react-icons/md';
import { useIcon } from '../Hooks/useIcon';
import AllIcons, {
  AllRanksIcons,
  FolderNames,
  GeneralIcons,
  Icons,
  JobSafetyIcons,
  OfficeIcons,
  ShipIcons,
} from './constants';

// ───── Styled Components ─────
const RootDiv = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const IconSelectIconsDiv = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  textAlign: 'center',
  border: '1px solid #ccc',
  borderRadius: 8,
  padding: 4,
  height: 400,
  overflowY: 'auto',
  alignContent: 'flex-start',
});

const PaperDiv = styled('div')(({ theme }: any) => ({
  position: 'absolute',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  padding: '5px',
  borderRadius: '4px',
  outline: 'none',
  width: 600,
}));

const IconElementDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: '10px 5px',
  cursor: 'pointer',
  width: '100px',
  padding: '5px',
  borderRadius: 4,
});

const IconFieldIconDiv = styled('div')({
  fontSize: 42,
  border: '1px solid #fff',
  borderRadius: 4,
});

const IconFolderIconDiv = styled('div')({
  fontSize: 42,
  padding: 5,
});

const IconNameDiv = styled('div')({
  fontSize: '.7rem',
  marginTop: '-.5rem',
  wordBreak: 'break-word',
  overflow: 'hidden',
});

// ───── Dummy Icon Data ─────

// ───── Main Component ─────
interface IconModalProps {
  handleSelectedIcon: (icon: string) => void;
}

export const IconModal: FC<IconModalProps> = ({ handleSelectedIcon }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState<string | null>(null);
  const [focusedIcon, setFocusedIcon] = useState('');

  const isSearching = search.length > 0;

  const displayedIcons = useMemo(() => {
    if (isSearching) {
      return AllIcons.filter((i) =>
        i.toLowerCase().includes(search.toLowerCase())
      );
    }
    switch (folder) {
      case 'All Ranks':
        return AllRanksIcons;
      case 'General':
        return GeneralIcons;
      case 'Job Safety':
        return JobSafetyIcons;
      case 'Office':
        return OfficeIcons;
      case 'Ship':
        return ShipIcons;
      case 'Misc':
        return Icons;
      default:
        return [];
    }
  }, [search, folder]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSearch('');
    setFolder(null);
    setFocusedIcon('');
  };

  return (
    <RootDiv>
      <Button variant="contained" onClick={handleOpen}>
        Select Icon
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PaperDiv>
          <div
            className="icon-select-header"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            {folder || isSearching ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MdArrowBack
                  style={{ fontSize: 32, cursor: 'pointer' }}
                  onClick={() => {
                    setFolder(null);
                    setSearch('');
                    setFocusedIcon('');
                  }}
                />
                <h3 style={{ marginLeft: 5 }}>
                  {isSearching ? 'Search Results' : folder}
                </h3>
              </div>
            ) : (
              <h3>Select Folder</h3>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label>Search:</label>
              <TextField
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <IconSelectIconsDiv>
            {!folder && !isSearching
              ? FolderNames.map((f) => (
                  <div
                    key={f}
                    style={{ height: 120 }}
                    onClick={() => setFolder(f)}
                  >
                    <IconElementDiv>
                      <IconFolderIconDiv>
                        {folder === f ? <FcOpenedFolder /> : <FcFolder />}
                      </IconFolderIconDiv>
                      <IconNameDiv>{f}</IconNameDiv>
                    </IconElementDiv>
                  </div>
                ))
              : displayedIcons.map((iconName, index) => (
                  <div
                    key={iconName + index.toString()}
                    onClick={() => setFocusedIcon(iconName)}
                  >
                    <IconElement
                      iconName={iconName}
                      isSelected={focusedIcon === iconName}
                    />
                  </div>
                ))}
          </IconSelectIconsDiv>

          <div
            className="actions"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 12,
            }}
          >
            <Button onClick={handleClose}>Close</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleSelectedIcon(focusedIcon);
                handleClose();
              }}
              disabled={!focusedIcon}
            >
              Apply
            </Button>
          </div>
        </PaperDiv>
      </Modal>
    </RootDiv>
  );
};

// ───── Reusable Icon Components ─────
interface IconElementProps {
  iconName: string;
  isSelected: boolean;
}

const IconElement: FC<IconElementProps> = ({ iconName, isSelected }) => {
  const Icon = useIcon({ iconName });
  return (
    <IconElementDiv
      style={{ border: isSelected ? '1px solid #e91e63' : 'none' }}
    >
      <IconFieldIconDiv>{Icon}</IconFieldIconDiv>
      <IconNameDiv>{iconName}</IconNameDiv>
    </IconElementDiv>
  );
};
